import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Badge, Inline } from 'braid-design-system';
import { Fragment, type FC } from 'react';

import { NewBadge } from 'src/components/common/NewBadge';
import { VerifiedCredentialsBadge } from 'src/components/common/VerifiedCredentialsBadge';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import { RelevantApplicationBadge } from '../RelevantApplicationBadge';

import translations from './.vocab';

interface ApplicationBadgesInOrder {
  shouldShowNewBadge: boolean;
  shouldShowRelevantApplicationBadge: boolean;
  shouldShowVerifiedCredentialsBadge: boolean;
  jobApplicationId: string;
  currentBucket: BUCKETS;
  language: Language;
}

export const ApplicationBadgesInOrder: FC<ApplicationBadgesInOrder> = ({
  shouldShowNewBadge,
  shouldShowRelevantApplicationBadge,
  shouldShowVerifiedCredentialsBadge,
  jobApplicationId,
  currentBucket,
  language,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  const badges = [];

  if (shouldShowRelevantApplicationBadge) {
    badges.push({
      name: t('Relevant application'),
      badge: (
        <RelevantApplicationBadge
          idSuffix={jobApplicationId}
          shouldShowTopPlacement={true}
        />
      ),
    });
  }

  if (shouldShowVerifiedCredentialsBadge) {
    badges.push({
      name: t('Verified credentials'),
      badge: <VerifiedCredentialsBadge idSuffix={jobApplicationId} />,
    });
  }

  return (
    <Inline space="xsmall" alignY="center">
      {shouldShowNewBadge ? <NewBadge /> : null}
      {badges
        .sort((a, b) => a.name.localeCompare(b.name, language))
        .map(({ name, badge }) => (
          <Fragment key={name}>{badge}</Fragment>
        ))}
      <Badge tone="neutral">{getLabelFromBucket(currentBucket)}</Badge>
    </Inline>
  );
};
